/**
 * P0-1 Engagement vocabulary contract (UI only).
 *
 * One concept = one word. Never use bare "Engaged" in user-facing copy.
 *
 * | Term              | Meaning                                      | Typical source              |
 * |-------------------|----------------------------------------------|-----------------------------|
 * | Decoys served     | Deception rows created (non-synthetic)       | deceptionsServed / count    |
 * | Time burned       | Decoys with time_wasted_ms > 0               | deceptionsEngaged (API)     |
 * | Time wasted       | Sum of delay ms                              | timeWastedMs                |
 * | Deep callbacks    | Multi-hop follow (ops-chain / canary)        | engagement_hops (canonical) |
 *
 * Product decision (2026-08-01 B1 probe):
 * - Canonical hop engagement is `engagement_hops` (+ canary_hits), not deceptions.engaged.
 * - July ops-chain fast path does NOT set deceptions.engaged — that column undercounts post-July.
 * - Do NOT dual-write ops-chain into deceptions.engaged (two sources of truth).
 * - deceptions.engaged remains legacy for classic UUID breadcrumb report path only.
 * See docs/research/B1_DEEP_CALLBACK_PROBE.md.
 */

export const ENGAGEMENT_VOCAB = {
  decoysServed: {
    label: 'Decoys Served',
    short: 'Served',
    hint: 'Fake pages, files and tarpit responses handed to suspicious visitors in this window. The total we generated — not a measure of whether anyone fell for them.',
  },
  timeBurned: {
    label: 'Time Burned',
    short: 'Time burned',
    hint: 'How many of those decoys actually held the visitor up (any delay applied). Usually most of Decoys Served — mass scanners count here. This is a count of decoys, not an amount of time.',
  },
  timeWasted: {
    label: 'Time Wasted',
    short: 'Time wasted',
    hint: 'The amount of attacker time those delays add up to. Time Burned counts how many decoys stalled someone; this is how long, in total.',
  },
  deepCallbacks: {
    label: 'Deep Callbacks',
    short: 'Callbacks',
    hint: 'Times an attacker took the bait and followed a decoy onward to a deeper planted path. The strongest signal here and deliberately rare — expect single or double digits, not thousands. Serving a decoy does not count; only the follow-up does.',
  },
  /** Session/threat badge: decoy applied delay (not deep callback). */
  decoyTimeBurned: {
    label: 'Decoy time burned',
    hint: 'A decoy response applied delay on this session.',
  },
  /** Table column for engaged=1 flag */
  callbackColumn: {
    label: 'Callback',
    yes: 'Yes',
    no: 'No',
    hint: 'Deep follow-up (breadcrumb/tracking), not mere delay.',
  },
  pathNode: {
    callback: 'CALLBACK',
    served: 'served',
    timeBurned: 'TIME BURNED',
  },
} as const;

/**
 * Hover copy for every Overview stat tile, and reused by Reports so the two
 * pages describe the same number the same way.
 *
 * Each string is written against the ACTUAL query in
 * packages/control-plane/src/routes/dashboard.ts `/stats`, and says how the
 * tile differs from its nearest sibling — the confusable pairs are
 * Sessions/Actors, Canary hits/Deep callbacks, and the four engagement tiles.
 */
export const OVERVIEW_TILE_HELP = {
  activeSessions:
    'Distinct visitor sessions still marked active in the selected window. One attacker rotating IPs can create many sessions — see Actors for the de-duplicated count.',
  actors:
    'Distinct attackers after clustering sessions by IP, time proximity and user-agent similarity. Always lower than Active Sessions; this is the "how many real people/bots" number. Synthetic test traffic excluded.',
  activeThreats:
    'Sessions scored high or critical, last seen in the window. A subset of Active Sessions — the ones worth looking at.',
  canaryHits:
    'Unlinked bait paths (planted only inside decoy content) that were requested. Proof someone read the decoy body. Counts the first touch; Deep Callbacks counts every onward hop.',
  fingerprints:
    'Distinct browser/device fingerprints first seen in the window. Identifies the same client across IP changes; feeds actor clustering.',
  avgConfidence:
    'Mean confidence score across sessions scored above "none" in the window. Higher means the scoring engine is more certain the traffic is hostile — not a measure of volume.',
} as const;

/** True if decoy applied any delay (time-burned). */
export function isTimeBurned(timeWastedMs: number | null | undefined): boolean {
  return (Number(timeWastedMs) || 0) > 0;
}

/** True if deep callback flag set. */
export function isDeepCallback(engaged: number | boolean | null | undefined): boolean {
  return engaged === 1 || engaged === true;
}

/**
 * Canonical deep-callback count for the UI.
 *
 * Prefers the hop-based `deepCallbacks` field (engagement_hops, follow kinds
 * only). Falls back to the legacy `deceptionsCallbacks` (deceptions.engaged=1)
 * ONLY when the control plane predates the hop fields — an older CP returns
 * `undefined`, and a tenant with genuinely zero callbacks returns 0, so the
 * `typeof` check must distinguish those two rather than using `||`.
 *
 * See docs/research/B1_DEEP_CALLBACK_PROBE.md.
 */
export function resolveDeepCallbacks(
  stats:
    | { deepCallbacks?: number | null; deceptionsCallbacks?: number | null }
    | null
    | undefined
): number {
  if (!stats) return 0;
  if (typeof stats.deepCallbacks === 'number') return stats.deepCallbacks;
  return Number(stats.deceptionsCallbacks) || 0;
}

/**
 * Time-burned count for one day of the Reports trend series.
 *
 * Reports previously summed `engaged` (deceptions.engaged = 1) and labelled it
 * "Time Burned". That is a different metric entirely: on 2026-08-01 ehaio's
 * 30-day figures were 5,298 decoys time-burned against 0 `engaged`, so the page
 * reported a 0% burn rate where the truth was 95.8%.
 *
 * Prefers `time_burned` from the control plane. Falls back to `engaged` ONLY
 * when the field is absent (older CP), never when it is a legitimate 0.
 */
export function resolveTimeBurnedDaily(
  row: { time_burned?: number | null; engaged?: number | null } | null | undefined
): number {
  if (!row) return 0;
  if (typeof row.time_burned === 'number') return row.time_burned;
  return Number(row.engaged) || 0;
}

/** Deep-callback count for one day of the Reports trend series (hop-based). */
export function resolveDeepCallbacksDaily(
  row: { deep_callbacks?: number | null } | null | undefined
): number {
  if (!row) return 0;
  return Number(row.deep_callbacks) || 0;
}

export function timeBurnRate(timeBurned: number, served: number): number {
  if (served <= 0) return 0;
  return (timeBurned / served) * 100;
}

export function callbackRate(callbacks: number, served: number): number {
  if (served <= 0) return 0;
  return (callbacks / served) * 100;
}
